import { useEffect, useState } from 'react'
import AppDropdown from '../../../../../components/AppDropdown'
import AppDateInput from '../../../../../components/AppDateInput'

const TAX_OPTIONS = [
  { value: '18% GST', label: '18% GST' },
  { value: '0% GST', label: '0% GST' },
  { value: '5% GST', label: '5% GST' },
  { value: '12% GST', label: '12% GST' },
  { value: '28% GST', label: '28% GST' },
]

function InstallmentsBanner({ onDismiss }) {
  return (
    <div className="relative rounded-2xl border border-primary/20 bg-primary/5 p-5 text-xs leading-relaxed text-on-surface shadow-sm">
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors cursor-pointer"
        title="Dismiss"
      >
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <span className="material-symbols-outlined text-primary text-[20px] shrink-0 mt-0.5">info</span>
        <div>
          <h4 className="mb-1.5 text-xs font-black uppercase tracking-[0.1em] text-primary">
            No Installments Created
          </h4>
          <ul className="space-y-1 list-disc list-inside text-on-surface-variant">
            <li>To create installment schedule, enter Fee details and click on the <strong>[GENERATE]</strong> button.</li>
            <li>Discount can be entered in percentage or directly as total discount amount.</li>
            <li>Tax will not be calculated on Courseware Fee. Tax report will calculate taxes payable at current rate, on actual amount received, irrespective of rate or amount entered here.</li>
            <li>Initial amount is the amount paid at the time of taking the admission. Day of month decides default day for installment. You can change the due date or amount after generating installments.</li>
            <li>Rounding will round installments to nearest 100/1000 and balance is adjusted in the last installment.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function FormFieldLabel({ label, required }) {
  return (
    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
      {label}{required ? <span className="text-error"> *</span> : null}
    </span>
  )
}

function FeeInput({ label, value, onChange, readOnly, subtext }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-44 shrink-0 text-xs font-semibold text-on-surface-variant">{label}</span>
      <div className="flex-1">
        <input
          type="text"
          value={value || '0'}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          readOnly={readOnly}
          className={`h-8 w-full rounded-xl border px-3 py-1.5 text-xs text-on-surface outline-none transition-all ${
            readOnly
              ? 'border-outline-variant/20 bg-surface-container-low text-on-surface-variant/70'
              : 'border-outline-variant/30 bg-surface-container-low focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20'
          }`}
        />
        {subtext && <p className="mt-0.5 text-[10px] text-on-surface-variant/60 italic">{subtext}</p>}
      </div>
    </div>
  )
}

function FeeSubtotal({ label, value }) {
  return (
    <div className="flex items-center gap-3 border-t border-outline-variant/20 pt-2.5">
      <span className="w-44 shrink-0 text-xs font-bold text-on-surface">{label}</span>
      <span className="flex-1 text-xs font-bold text-primary font-mono">{value || '0.00'}</span>
    </div>
  )
}

function FeeDiscountRow({ value, onChange, taxChecked, onTaxChange, taxRate, onTaxRateChange, taxAmount }) {
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="w-44 shrink-0 text-xs font-semibold text-on-surface-variant">Discount</span>
        <input
          type="text"
          value={value || '0'}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-28 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-1.5 text-xs text-on-surface outline-none transition-all focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20"
        />
      </div>
      <FeeSubtotal label="Sub Total" value="0.00" />
      <div className="flex items-center gap-3">
        <span className="w-44 shrink-0 text-xs font-semibold text-on-surface-variant">Tax</span>
        <div className="flex items-center gap-3 flex-1">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={taxChecked}
              onChange={(e) => onTaxChange(e.target.checked)}
              className="h-4 w-4 rounded-md border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
            />
          </label>
          <div className="w-36">
            <AppDropdown
              value={taxRate}
              onChange={onTaxRateChange}
              options={TAX_OPTIONS}
              triggerClassName="!h-8 !rounded-xl !border-outline-variant/30 !bg-surface-container-low !text-xs"
            />
          </div>
          <span className="text-xs font-mono font-semibold text-on-surface-variant">₹{taxAmount || '0'}</span>
        </div>
      </div>
      <FeeSubtotal label="Sub Total" value="0.00" />
    </>
  )
}

function InstallmentTable({ rows = [], onAdd, onDelete, onUpdateDate, fmtDate }) {
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-primary text-on-primary">
              <th className="px-4 py-3 font-bold uppercase tracking-wider">Due Date</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider">Particulars</th>
              <th className="px-4 py-3 font-bold uppercase tracking-wider">Amount</th>
              <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-xs font-bold leading-relaxed text-blue-700">
                      No entries meet selected criteria — Either there are no entries in the selected module
                      or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.key ?? row.id}
                  className={`border-b border-outline-variant/10 last:border-0 transition-colors hover:bg-surface-container-low/40 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-surface-container-low/30'
                  }`}
                >
                  <td className="px-4 py-2.5 font-medium text-on-surface">
                    {fmtDate ? fmtDate(row.dueDate) : row.dueDate}
                  </td>
                  <td className="px-4 py-2.5 text-on-surface">{row.particulars}</td>
                  <td className="px-4 py-2.5 font-bold font-mono text-on-surface">₹{row.amount}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => onDelete(row.id, row.key)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-error hover:bg-error-container/40 transition-colors cursor-pointer"
                      title="Delete Installment"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-outline-variant/20 bg-surface-container-low px-4 py-2 text-xs">
          <span className="font-bold text-on-surface">Total Installment Amount:</span>
          <span className="font-mono font-black text-primary text-sm">
            ₹{rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline cursor-pointer"
        >
          <span className="material-symbols-outlined text-[15px]">add</span>
          Add Installment
        </button>
      </div>
    </div>
  )
}

export function FeesTab({ form = {}, patchForm = () => {}, readOnly = false }) {
  const [bannerVisible, setBannerVisible] = useState(true)
  const [admissionFee, setAdmissionFee] = useState(form.feeAdmission || '')
  const [tuitionFee, setTuitionFee] = useState(form.feesTuition || '')
  const [appFee, setAppFee] = useState('')
  const [securityDeposit, setSecurityDeposit] = useState('')
  const [monthlyTuition, setMonthlyTuition] = useState('')
  const [quarterlyTuition, setQuarterlyTuition] = useState('')
  const [monthlyTransport, setMonthlyTransport] = useState('')
  const [courseWare, setCourseWare] = useState(form.feeCourseware || '')
  const [quarterlyTransport, setQuarterlyTransport] = useState('')
  const [othersFee, setOthersFee] = useState('')
  const [discount, setDiscount] = useState(form.discAmt || '')
  const [taxChecked, setTaxChecked] = useState(form.srTaxYN || false)
  const [taxRate, setTaxRate] = useState(form.taxPercent && form.taxPercent !== 'Select' ? form.taxPercent : '18% GST')
  const [writeOff, setWriteOff] = useState('')

  const [installments, setInstallments] = useState(form.installments || 0)
  const [intervalMonths, setIntervalMonths] = useState('2')
  const [dayOfMonth, setDayOfMonth] = useState('10')
  const [initialAmt, setInitialAmt] = useState('0')
  const [rounding, setRounding] = useState('100')

  const [installmentRows, setInstallmentRows] = useState(form.feesInstallments || [])

  const num = (v) => parseFloat(v) || 0

  useEffect(() => {
    setInstallmentRows(form.feesInstallments || [])
  }, [form.feesInstallments])

  const commitRows = (next) => {
    setInstallmentRows(next)
    if (patchForm) {
      patchForm('feesInstallments', next)
      patchForm('feesInstallmentsDirty', true)
    }
  }

  const isoDate = (y, m, d) =>
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`

  const fmtDate = (d) => {
    if (!d) return ''
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (m) {
      const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    return d
  }

  const subTotal1 = num(admissionFee) + num(tuitionFee) + num(appFee) + num(securityDeposit) + num(monthlyTuition) + num(quarterlyTuition) + num(monthlyTransport)
  const subTotal2 = subTotal1 - num(discount)
  const taxAmt = taxChecked ? Math.round(subTotal2 * 0.18) : 0
  const subTotal3 = subTotal2 + taxAmt
  const totalAmount = subTotal3 + num(courseWare) + num(quarterlyTransport) + num(othersFee)

  const handleGenerate = () => {
    const rows = []
    const numInstallments = num(installments)
    if (numInstallments <= 0) return
    const amountPerInstallment = (totalAmount - num(initialAmt)) / numInstallments
    const start = new Date()
    for (let i = 0; i < numInstallments; i++) {
      const base = new Date(start.getFullYear(), start.getMonth() + i * num(intervalMonths), num(dayOfMonth))
      const dueDate = isoDate(base.getFullYear(), base.getMonth() + 1, base.getDate())
      rows.push({
        key: Date.now() + i,
        id: 0,
        dueDate,
        particulars: `Installment ${i + 1}`,
        amount: rounding === '100' ? Math.round(amountPerInstallment / 100) * 100 : rounding === '1000' ? Math.round(amountPerInstallment / 1000) * 1000 : Math.round(amountPerInstallment),
      })
    }
    if (rows.length > 0) {
      rows[rows.length - 1].amount = totalAmount - num(initialAmt) - rows.slice(0, -1).reduce((s, r) => s + r.amount, 0)
    }
    commitRows(rows)
  }

  const addInstallmentRow = () => {
    commitRows([
      ...installmentRows,
      {
        key: Date.now(),
        id: 0,
        dueDate: new Date().toISOString().slice(0, 10),
        particulars: `Installment ${installmentRows.length + 1}`,
        amount: '0',
      },
    ])
  }

  const deleteInstallmentRow = (id, key) => {
    commitRows(installmentRows.filter((r) => r.key !== key && !(r.id !== 0 && r.id === id)))
  }

  return (
    <div className="space-y-6">
      {bannerVisible && (
        <InstallmentsBanner onDismiss={() => setBannerVisible(false)} />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-5">
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
            <h3 className="mb-4 text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2.5">
              Fee Details
            </h3>
            <div className="space-y-3">
              <FeeInput label="Admission Fees" value={admissionFee} onChange={(v) => { setAdmissionFee(v); patchForm('feeAdmission', v) }} readOnly={readOnly} />
              <FeeInput label="Tuition Fees" value={tuitionFee} onChange={(v) => { setTuitionFee(v); patchForm('feesTuition', v) }} readOnly={readOnly} />
              <FeeInput label="Application Fee" value={appFee} onChange={setAppFee} readOnly={readOnly} />
              <FeeInput label="Security Deposit" value={securityDeposit} onChange={setSecurityDeposit} readOnly={readOnly} />
              <FeeInput label="Monthly Tuition Fee" value={monthlyTuition} onChange={setMonthlyTuition} readOnly={readOnly} />
              <FeeInput label="Quarterly Tuition Fee" value={quarterlyTuition} onChange={setQuarterlyTuition} readOnly={readOnly} />
              <FeeInput label="Monthly Transport Fee" value={monthlyTransport} onChange={setMonthlyTransport} readOnly={readOnly} />
              <FeeSubtotal label="Sub Total" value={subTotal1.toFixed(2)} />
              <FeeDiscountRow
                value={discount}
                onChange={(v) => { setDiscount(v); patchForm('discAmt', v) }}
                taxChecked={taxChecked}
                onTaxChange={(v) => { setTaxChecked(v); patchForm('srTaxYN', v) }}
                taxRate={taxRate}
                onTaxRateChange={(v) => { setTaxRate(v); patchForm('taxPercent', v) }}
                taxAmount={taxAmt}
              />
              <FeeInput label="CourseWare" value={courseWare} onChange={(v) => { setCourseWare(v); patchForm('feeCourseware', v) }} readOnly={readOnly} />
              <FeeInput label="Quarterly Transport Fee" value={quarterlyTransport} onChange={setQuarterlyTransport} readOnly={readOnly} />
              <FeeInput label="Others Fee" value={othersFee} onChange={setOthersFee} readOnly={readOnly} />
              <div className="border-t-2 border-outline-variant/30 pt-2.5">
                <FeeSubtotal label="Total Amount" value={totalAmount.toFixed(2)} />
              </div>
              <FeeInput label="Write Off" value={writeOff} onChange={setWriteOff} subtext="Use write-off to record bad-debts" readOnly={readOnly} />
            </div>
          </div>
        </div>

        <div className="space-y-4 lg:col-span-7">
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
            <h3 className="mb-4 text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2.5">
              Installment Generator
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <label className="grid gap-1">
                <FormFieldLabel label="Installment" />
                <input
                  type="number"
                  value={installments}
                  onChange={(e) => setInstallments(e.target.value)}
                  disabled={readOnly}
                  className="h-8 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs text-on-surface outline-none focus:border-primary focus:bg-surface focus:ring-1 focus:ring-primary/20"
                />
              </label>
              <label className="grid gap-1">
                <FormFieldLabel label="Interval-Mo" />
                <input
                  type="number"
                  value={intervalMonths}
                  onChange={(e) => setIntervalMonths(e.target.value)}
                  disabled={readOnly}
                  className="h-8 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs text-on-surface outline-none focus:border-primary focus:bg-surface focus:ring-1 focus:ring-primary/20"
                />
              </label>
              <label className="grid gap-1">
                <FormFieldLabel label="Day Of Mo" />
                <input
                  type="number"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  disabled={readOnly}
                  className="h-8 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs text-on-surface outline-none focus:border-primary focus:bg-surface focus:ring-1 focus:ring-primary/20"
                />
              </label>
              <label className="grid gap-1">
                <FormFieldLabel label="Initial Amt" />
                <input
                  type="text"
                  value={initialAmt}
                  onChange={(e) => setInitialAmt(e.target.value)}
                  disabled={readOnly}
                  className="h-8 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs text-on-surface outline-none focus:border-primary focus:bg-surface focus:ring-1 focus:ring-primary/20"
                />
              </label>
              <div className="grid gap-1">
                <FormFieldLabel label="Round" />
                <div className="flex gap-1">
                  {['100', '1000', 'None'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setRounding(opt)}
                      disabled={readOnly}
                      className={`flex-1 rounded-lg border px-1 py-1 text-[10px] font-bold transition-all cursor-pointer ${
                        rounding === opt
                          ? 'border-primary bg-primary text-on-primary shadow-xs'
                          : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1 justify-end">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={readOnly}
                  className="h-8 rounded-xl bg-primary px-3 text-xs font-bold text-on-primary shadow-md shadow-primary/20 hover:bg-primary/90 transition-all cursor-pointer"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>

          <InstallmentTable
            rows={installmentRows}
            onAdd={addInstallmentRow}
            onDelete={deleteInstallmentRow}
            fmtDate={fmtDate}
          />
        </div>
      </div>
    </div>
  )
}
